import { useMutation } from '@tanstack/react-query';
import { Mail, Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type {
  SendInstallInstructionsData,
  SendInstallInstructionsResponse,
} from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { DOCS_WEBSITE_URL, createWidgetSnippet } from '@/UI/Website/Install/installSnippets';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mirror the backend caps (MAX_INSTALL_INSTRUCTIONS_NOTE_LENGTH, the emails
// conlist max) so the form can't submit a payload the endpoint will 422.
const MAX_NOTE_LENGTH = 2000;
const MAX_RECIPIENTS = 10;

const parseEmails = (raw: string): string[] =>
  raw
    .split(/[,\n;]+/)
    .map((email) => email.trim())
    .filter(Boolean);

interface EmailToDeveloperDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenarioId: string;
  onSent?: () => void;
}

export const EmailToDeveloperDialog = ({
  open,
  onOpenChange,
  scenarioId,
  onSent,
}: EmailToDeveloperDialogProps) => {
  const { t } = useTranslation('website');
  const [emails, setEmails] = useState('');
  const [note, setNote] = useState('');

  const mutation = useMutation<SendInstallInstructionsResponse, Error, SendInstallInstructionsData>(
    {
      mutationKey: [QueryKey.installInstructions.send],
      onSuccess: () => {
        toast.success(t('install.email_dialog.success'));
        setEmails('');
        setNote('');
        onSent?.();
        onOpenChange(false);
      },
      onError: () => toast.error(t('install.email_dialog.error')),
    },
  );

  const parsed = parseEmails(emails);
  const tooMany = parsed.length > MAX_RECIPIENTS;
  const allValid = parsed.length > 0 && !tooMany && parsed.every((email) => EMAIL_RE.test(email));
  const showInvalid = emails.trim().length > 0 && !allValid;

  const handleSend = () => {
    if (!allValid) return;
    mutation.mutate({ emails: parsed, note: note.trim() || undefined });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="email-dev-dialog">
        <DialogHeader>
          <DialogTitle>
            <Mail className="size-5" />
            {t('install.email_dialog.title')}
          </DialogTitle>
          <DialogDescription>{t('install.email_dialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="dev-emails">{t('install.email_dialog.email_label')}</Label>
          <Input
            id="dev-emails"
            size="sm"
            type="text"
            value={emails}
            error={showInvalid}
            onChange={(event) => setEmails(event.target.value)}
            placeholder={t('install.email_dialog.email_placeholder')}
            data-testid="email-dev-input"
          />
          <p
            className={showInvalid ? 'text-xs text-destructive' : 'text-xs text-foreground-subtle'}
          >
            {showInvalid
              ? tooMany
                ? t('install.email_dialog.too_many_emails', { max: MAX_RECIPIENTS })
                : t('install.email_dialog.invalid_email')
              : t('install.email_dialog.email_hint')}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="dev-note">{t('install.email_dialog.note_label')}</Label>
          <AutosizeTextarea
            id="dev-note"
            minHeight={56}
            maxLength={MAX_NOTE_LENGTH}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('install.email_dialog.note_placeholder')}
            data-testid="email-dev-note"
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t('install.email_dialog.preview_label')}</Label>
          <div className="space-y-2 rounded-xl border border-border bg-muted p-3 text-xs text-muted-foreground">
            <p>{t('install.email.intro')}</p>
            <p>{t('install.email.snippet_intro')}</p>
            <pre className="overflow-x-auto whitespace-pre font-mono leading-relaxed">
              {createWidgetSnippet(scenarioId)}
            </pre>
            <p className="truncate">{t('install.email.guide_line', { url: DOCS_WEBSITE_URL })}</p>
          </div>
        </div>

        <Button
          variant="dark"
          size="md"
          className="w-full justify-center"
          disabled={!allValid || mutation.isPending}
          onClick={handleSend}
          data-testid="email-dev-send"
        >
          {mutation.isPending ? <Spinner className="size-4" /> : <Send className="size-4" />}
          {t('install.email_dialog.send')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
